// Conferência do Relatório de Créditos antes de gerar o PDF.
//
// O modal existe por causa de um número: 19% das UCs com fatura não têm NENHUM mês com
// informação de créditos completa, e outras têm o dado só em parte dos meses. Gerar o
// PDF direto no clique entregaria ao associado um total silenciosamente menor do que a
// realidade. Aqui o operador vê a mesma tabela que vai sair impressa, com os meses
// faltando marcados, e decide.
//
// A prévia é montada com `montarRelatorioCreditos` — a MESMA função da edge que
// desenha o PDF. Se a tela e o papel divergissem, a conferência não valeria nada.
//
// Visual: segue o Financeiro (cards de KPI, tabela com cabeçalho cinza, primary nos
// botões). O que muda é o cuidado com número: coluna numérica em `tabular-nums`, para
// as casas decimais alinharem e o olho pegar a ordem de grandeza errada de longe.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ArrowRight, Ban, Download, FileText, Info, Loader2, X, Zap, Wallet } from 'lucide-react';

import { UnidadeConsumidora } from '../../types';
import { SEM_INFORMACAO, formatarKwh, textoDoAviso } from '../../src/lib/relatorioCreditos';
import { NAO_INFORMADO, ROTULO_SITUACAO, formatarReaisOuNI } from '../../src/lib/relatorioCreditosFinanceiro';
import {
  MESES_PADRAO,
  carregarPrevia,
  gerarPdfRelatorioCreditos,
  type PreviaRelatorio,
} from '../../src/lib/relatorioCreditosApi';
import { logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  unidade: UnidadeConsumidora | null;
}

/** Card de total. `destaque` marca o número que o associado vai procurar primeiro. */
const CardTotal: React.FC<{ rotulo: string; valor: string; destaque?: boolean }> = ({ rotulo, valor, destaque }) => (
  <div
    className={`flex-1 rounded-xl border p-4 ${
      destaque
        ? 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/40 dark:bg-emerald-900/10'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40'
    }`}
  >
    <p
      className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${
        destaque ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500'
      }`}
    >
      {rotulo}
    </p>
    <p
      className={`text-lg font-bold tabular-nums ${
        destaque ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-white'
      }`}
    >
      {valor}
    </p>
  </div>
);

const RelatorioCreditosModal: React.FC<Props> = ({ isOpen, onClose, unidade }) => {
  const { user } = useAuth();
  const [previa, setPrevia] = useState<PreviaRelatorio | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aba, setAba] = useState<'energia' | 'financeiro'>('energia');
  const [mesInicio, setMesInicio] = useState<string>('');
  const [mesFim, setMesFim] = useState<string>('');
  // Fecha só quando o gesto COMEÇOU no backdrop: sem isso, soltar o mouse fora do
  // card ao selecionar um número da tabela fecha o modal no meio da conferência.
  const backdropMouseDownRef = useRef(false);

  const numeroUc = unidade?.numero_uc ?? '';

  // Primeira carga: sem recorte, para descobrir quais meses existem.
  useEffect(() => {
    if (!isOpen || !numeroUc) return;
    let vivo = true;
    setCarregando(true);
    setErro(null);
    carregarPrevia(numeroUc, unidade?.outro_numero_uc)
      .then(p => {
        if (!vivo) return;
        // Padrão: os 12 últimos meses. Um relatório de dois anos vira uma parede de
        // tabela que ninguém confere de verdade.
        const meses = p.mesesDisponiveis;
        setMesInicio(meses.length > MESES_PADRAO ? meses[meses.length - MESES_PADRAO] : meses[0] ?? '');
        setMesFim(meses[meses.length - 1] ?? '');
        setPrevia(p);
      })
      .catch(e => vivo && setErro(e instanceof Error ? e.message : 'Falha ao carregar as faturas.'))
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [isOpen, numeroUc, unidade?.outro_numero_uc]);

  // Recarga quando o operador mexe no período. `mesesDisponiveis` vem da primeira
  // carga e não pode ser sobrescrito pelo recorte, ou os seletores se estreitariam
  // a cada mudança até sobrar um mês só.
  const recarregar = useCallback(async (inicio: string, fim: string) => {
    if (!numeroUc) return;
    setCarregando(true);
    setErro(null);
    try {
      const p = await carregarPrevia(numeroUc, unidade?.outro_numero_uc, { inicio, fim });
      setPrevia(prev => ({ ...p, mesesDisponiveis: prev?.mesesDisponiveis ?? p.mesesDisponiveis }));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar as faturas.');
    } finally {
      setCarregando(false);
    }
  }, [numeroUc, unidade?.outro_numero_uc]);

  useEffect(() => {
    if (!isOpen) { setPrevia(null); setErro(null); return; }
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  const relatorio = previa?.relatorio ?? null;
  const financeiro = previa?.financeiro ?? null;
  const semDado = relatorio ? relatorio.mesesTotal - relatorio.mesesComDado : 0;
  const avisos = useMemo(() => relatorio?.avisos.map(textoDoAviso) ?? [], [relatorio]);

  const handleGerar = async () => {
    if (!unidade || !numeroUc) return;
    setGerando(true);
    setErro(null);
    try {
      const blob = await gerarPdfRelatorioCreditos({
        unidadeId: unidade.id,
        ucCodigo: numeroUc,
        mesInicio: mesInicio || null,
        mesFim: mesFim || null,
      });
      // Abre em aba nova: o operador confere o documento antes de mandar ao associado,
      // e o download fica a um clique dentro do próprio visualizador.
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Folga para o visualizador ler o blob antes de soltar a URL.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      logUsageEvent({
        userId: user?.id,
        modulo: 'financeiro',
        funcionalidade: 'relatorio_creditos',
        acao: 'gerar_pdf',
        origem: 'acao',
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao gerar o relatório.');
    } finally {
      setGerando(false);
    }
  };

  if (!isOpen || !unidade) return null;

  const meses = previa?.mesesDisponiveis ?? [];
  const vazio = !carregando && !erro && relatorio && relatorio.linhas.length === 0;
  const classeSelect =
    'mt-1 block w-36 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-normal normal-case tracking-normal text-gray-900 dark:text-white focus:outline-none focus:border-primary disabled:opacity-50';

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
      onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
      onClick={() => { if (backdropMouseDownRef.current) onClose(); backdropMouseDownRef.current = false; }}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col m-4 border border-gray-200 dark:border-gray-800"
        onClick={e => e.stopPropagation()}
      >
        {/* Cabeçalho */}
        <div className="flex-shrink-0 px-6 pt-6 pb-5 border-b border-gray-100 dark:border-gray-800">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-poppins font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText size={20} className="text-primary" />
                Relatório de Créditos de Energia
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Confira os números antes de gerar o PDF · UC {numeroUc} · {unidade.nome_cliente}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-3 mt-5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              De
              <select
                value={mesInicio}
                onChange={e => { setMesInicio(e.target.value); recarregar(e.target.value, mesFim); }}
                disabled={carregando || !meses.length}
                className={classeSelect}
              >
                {meses.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Até
              <select
                value={mesFim}
                onChange={e => { setMesFim(e.target.value); recarregar(mesInicio, e.target.value); }}
                disabled={carregando || !meses.length}
                className={classeSelect}
              >
                {meses.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            {relatorio && relatorio.mesesTotal > 0 && (
              <p className="text-xs text-gray-500 pb-2.5">
                {relatorio.mesesComDado} de {relatorio.mesesTotal}{' '}
                {relatorio.mesesTotal === 1 ? 'mês tem' : 'meses têm'} informação de créditos
              </p>
            )}
          </div>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {carregando && (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
              <Loader2 size={18} className="animate-spin" /> Carregando as faturas...
            </div>
          )}

          {!carregando && erro && (
            <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{erro}</span>
            </div>
          )}

          {vazio && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Ban size={28} className="text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                Nenhuma fatura no período escolhido
              </p>
              <p className="text-xs text-gray-500 mt-1">Amplie o período ou confira o cadastro da unidade.</p>
            </div>
          )}

          {/* `financeiro` na guarda de propósito: o projeto compila sem
              strictNullChecks, então o acesso a `financeiro.linhas` na aba de reais
              não seria acusado pelo tsc — seria um erro em runtime. */}
          {!carregando && !erro && relatorio && financeiro && relatorio.linhas.length > 0 && (
            <>
              {/* Enviado -> compensado -> saldo: a conta do relatório em uma linha. */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5">
                <CardTotal rotulo="Excedente enviado pela Vigor" valor={formatarKwh(relatorio.totais.excedenteVigor)} />
                <ArrowRight size={16} className="hidden sm:block flex-shrink-0 text-gray-300 dark:text-gray-600" />
                <CardTotal rotulo="Energia compensada" valor={formatarKwh(relatorio.totais.compensadaVigor)} />
                <ArrowRight size={16} className="hidden sm:block flex-shrink-0 text-gray-300 dark:text-gray-600" />
                <CardTotal rotulo="Saldo da Vigor" valor={formatarKwh(relatorio.totais.saldoVigor)} destaque />
              </div>

              {/* Avisos: é o que o operador precisa ler antes de mandar ao associado. */}
              {avisos.length > 0 && (
                <div className="space-y-2 mb-5">
                  {avisos.map((texto, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm"
                    >
                      <Info size={15} className="mt-0.5 flex-shrink-0" />
                      <span>{texto}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Energia em kWh e financeiro em R$ são conferidos em abas separadas,
                  pela mesma razão que o modelo os separa no PDF: são coisas distintas,
                  e o operador que confere uma não está conferindo a outra. */}
              <div className="flex gap-1 mb-3 border-b border-gray-200 dark:border-gray-800">
                {([['energia', 'Energia (kWh)', Zap], ['financeiro', 'Financeiro (R$)', Wallet]] as const).map(
                  ([id, rotulo, Icone]) => (
                    <button
                      key={id}
                      onClick={() => setAba(id)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                        aba === id
                          ? 'border-primary text-primary'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      <Icone size={15} /> {rotulo}
                    </button>
                  )
                )}
              </div>

              {aba === 'financeiro' ? (
                <>
                  <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800/60">
                        <tr>
                          <th className="px-4 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300">Referência</th>
                          <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Contribuição</th>
                          <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Emitido</th>
                          <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Pagamentos</th>
                          <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Em aberto</th>
                          <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Situação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financeiro.linhas.map((l, i) => (
                          <tr
                            key={`${l.mesReferencia}-${i}`}
                            className={`border-t border-gray-100 dark:border-gray-800 ${
                              l.temDado ? '' : 'bg-red-50/60 dark:bg-red-900/10'
                            }`}
                          >
                            <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                              {l.mesReferencia || '—'}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatarReaisOuNI(l.contribuicaoCalculada)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatarReaisOuNI(l.valorEmitido)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatarReaisOuNI(l.pagamentos)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatarReaisOuNI(l.saldoEmAberto)}</td>
                            <td
                              className={`px-4 py-2.5 text-right whitespace-nowrap ${
                                l.situacao === 'vencida'
                                  ? 'text-red-600 dark:text-red-400 font-semibold'
                                  : l.situacao === 'indefinida'
                                    ? 'text-gray-400'
                                    : 'text-gray-600 dark:text-gray-300'
                              }`}
                            >
                              {ROTULO_SITUACAO[l.situacao]}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 font-bold">
                          <td className="px-4 py-2.5 text-gray-900 dark:text-white">TOTAL</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-white">{formatarReaisOuNI(financeiro.totais.contribuicaoCalculada)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-white">{formatarReaisOuNI(financeiro.totais.totalEmitido)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-white">{formatarReaisOuNI(financeiro.totais.pagamentos)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-white">{formatarReaisOuNI(financeiro.totais.saldoEmAberto)}</td>
                          <td className="px-4 py-2.5" />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    {NAO_INFORMADO} não é zero: quer dizer que o sistema não tem o dado.
                    Contribuição é o valor apurado do ciclo; emitido é o que virou cobrança de
                    verdade. Os dois saem separados no PDF de propósito.
                  </p>
                </>
              ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300">Referência</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Recebidos</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Utilizados</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Variação</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Saldo SCEE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.linhas.map((l, i) => (
                      <tr
                        key={`${l.mesReferencia}-${i}`}
                        className={`border-t border-gray-100 dark:border-gray-800 ${
                          l.temDado ? '' : 'bg-red-50/60 dark:bg-red-900/10'
                        }`}
                      >
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                          {l.mesReferencia || '—'}
                        </td>
                        {l.temDado ? (
                          <>
                            <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                              {formatarKwh(l.excedenteVigor)}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                              {formatarKwh(l.compensadaVigor)}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                              {formatarKwh(l.saldoPeriodo)}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                              {l.saldoSceeDistribuidora === null ? '—' : formatarKwh(l.saldoSceeDistribuidora)}
                            </td>
                          </>
                        ) : (
                          <td colSpan={4} className="px-4 py-2.5">
                            <span className="flex items-center justify-end gap-1.5 text-red-600 dark:text-red-400">
                              <AlertTriangle size={13} />
                              {SEM_INFORMACAO}
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 font-bold">
                      <td className="px-4 py-2.5 text-gray-900 dark:text-white">TOTAL</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-white">
                        {formatarKwh(relatorio.totais.excedenteVigor)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-white">
                        {formatarKwh(relatorio.totais.compensadaVigor)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-white">
                        {formatarKwh(relatorio.totais.saldoVigor)}
                      </td>
                      <td className="px-4 py-2.5" />
                    </tr>
                  </tbody>
                </table>
              </div>
              )}
            </>
          )}
        </div>

        {/* Rodapé */}
        <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-800">
          <p className="text-xs text-gray-500 max-w-md">
            {semDado > 0
              ? `${semDado} ${semDado === 1 ? 'mês sairá' : 'meses sairão'} no PDF como "${SEM_INFORMACAO}" e ${semDado === 1 ? 'fica' : 'ficam'} fora dos totais.`
              : 'Todos os meses do período têm informação de créditos.'}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleGerar}
              disabled={gerando || carregando || !relatorio?.linhas.length}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {gerando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {gerando ? 'Gerando...' : 'Gerar PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RelatorioCreditosModal;
